<script setup lang="ts">
import { mdToHtml } from '~/lib/markdown'
import { GTMContentGroup } from '~/lib/models/gtm'
import { contentService } from '~/lib/services/content/content.service'

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'accept'): void
}>()

const origin = useSiteDomain({ protocol: false, noOverride: false })
const { t } = useLocale()
const { trackPageView, trackOnCloseModal, trackClickCTA } = useGTM()

const modal = useTemplateRef('modal')
const content = ref(t('mandatoryTerms.default'))
const loading = ref(true)
const disabledAcceptBtn = ref(true)

const onScrolledBottom = () => {
  if (loading.value) return
  disabledAcceptBtn.value = false
}

const onClose = () => {
  emit('close')
  trackOnCloseModal()
}

const onAccept = () => {
  trackClickCTA()
  emit('accept')
  trackOnCloseModal()
}

onMounted(async () => {
  trackPageView(
    {
      cg: GTMContentGroup.primary.USER,
      cg2: GTMContentGroup.secondary.TERMS_AND_CONDITIONS,
    },
    true
  )
  const text = await contentService.getTermsByDomain(origin)
  if (text) {
    content.value = text
  }
  loading.value = false
  modal?.value?.delayedCheckScrolledBottom()
})
</script>

<template>
  <ModalCenter
    ref="modal"
    :add-close-button="false"
    no-actions-class
    @close="onClose"
    @scrolled-bottom="onScrolledBottom"
  >
    <template v-if="!loading" #header>
      <div class="mx-2 mb-6 mt-14 flex flex-col gap-4 md:mx-0 md:mt-10 md:gap-6">
        <h1 class="text-3xl font-bold">{{ t('mandatoryTerms.title') }}</h1>
        <p class="text-sm font-normal">
          {{ t('mandatoryTerms.subtitle') }}
        </p>
      </div>
    </template>

    <template #content>
      <!-- Loading -->
      <section v-if="loading" class="flex min-h-full flex-col justify-center md:min-h-72">
        <Loader fit />
      </section>

      <!-- Content -->
      <section
        v-else
        v-dompurify-html="mdToHtml(content)"
        class="prose min-h-full max-w-none px-4 text-xs text-mcd-black prose-strong:text-mcd-black md:ml-14 md:mr-10 md:min-h-72 md:px-0 md:text-mcd-secondaryDarkGrey md:prose-strong:text-mcd-secondaryDarkGrey"
      ></section>
    </template>

    <template v-if="!loading" #actions>
      <div class="flex flex-col items-center px-4 py-8 md:mx-14 md:mb-6 md:items-end">
        <McdButton
          is-primary
          class="w-full py-3 text-sm md:w-fit"
          :disabled="disabledAcceptBtn"
          @on-click="onAccept"
        >
          {{ t('mandatoryTerms.action') }}
        </McdButton>
      </div>
    </template>
  </ModalCenter>
</template>
